import type { CSSProperties } from 'react';
import { BREAKPOINTS, type Breakpoint, type Space } from '../styles/tokens';
import { useMediaQuery } from './media-query';

export type ResponsiveProperties<T = string> = {
  [breakpoint in Breakpoint]?: T;
};

export type ResponsiveValue<T = string> = T | ResponsiveProperties<T>;

export function buildResponsiveVariableName(component: string, propName: string, breakpoint: Breakpoint) {
  return `--ixf-${component}-${propName}-${breakpoint}`;
}

export function buildResponsiveVariableValue<T extends { toString(): string } = string>(value: T, prefix?: string) {
  return prefix ? `var(--ixf-${prefix}-${value.toString()})` : value.toString();
}

export function buildResponsiveProps<T = string>(
  component: string,
  propName: string,
  responsiveProperties?: ResponsiveValue<T>,
  variableValuePrefix?: string,
  variableBuilder?: (value: T, breakpoint: Breakpoint) => string | number,
) {
  if (responsiveProperties === undefined || responsiveProperties === null) {
    return {};
  }

  if (['string', 'number', 'boolean'].includes(typeof responsiveProperties)) {
    const variableValue = buildResponsiveVariableValue(responsiveProperties, variableValuePrefix);
    const variableName = buildResponsiveVariableName(component, propName, BREAKPOINTS[0]);
    const builtValue = variableBuilder ? variableBuilder(variableValue as T, BREAKPOINTS[0]) : variableValue;

    return {
      [variableName]: builtValue,
    };
  }

  return Object.entries(responsiveProperties).reduce((accum, [breakpoint, value]) => {
    accum[buildResponsiveVariableName(component, propName, breakpoint as Breakpoint) as keyof CSSProperties] = variableBuilder
      ? variableBuilder(value, breakpoint as Breakpoint)
      : (buildResponsiveVariableValue(value, variableValuePrefix) as any);

    return accum;
  }, {} as CSSProperties);
}

// TODO: improve design type generation to include responsive sizes so this doesn't need to be hardcoded.
const breakpoints = {
  sm: '(max-width: 900px)',
  md: '(max-width: 1200px)',
  lg: '(max-width: 1600px)',
  xl: '(max-width: 1900px)',
  xxl: '(max-width: 2300px)',
} as const;

const orderedBreakpoints = new Map<Breakpoint, string>([
  ['sm', breakpoints.sm],
  ['md', breakpoints.md],
  ['lg', breakpoints.lg],
  ['xl', breakpoints.xl],
  ['xxl', breakpoints.xxl],
]);

export function useBreakpoint(breakpoint: Breakpoint) {
  return useMediaQuery(breakpoints[breakpoint]);
}

export function useCurrentBreakpoint() {
  const isSm = useBreakpoint('sm');
  const isMd = useBreakpoint('md');
  const isLg = useBreakpoint('lg');
  const isXl = useBreakpoint('xl');
  const isXxl = useBreakpoint('xxl');

  return ([isSm ? 'sm' : undefined, isMd ? 'md' : undefined, isLg ? 'lg' : undefined, isXl ? 'xl' : undefined, isXxl ? 'xxl' : undefined].filter(
    Boolean,
  )[0] || 'sm') as Breakpoint;
}

export function useCurrentBreakpointValue<T extends string = string>(responsiveValue: ResponsiveValue<T>): T | undefined {
  const currentBreakpoint = useCurrentBreakpoint();

  if (typeof responsiveValue === 'object' && responsiveValue) {
    let closestBreakpointValue: T | undefined;

    for (const [breakpoint] of orderedBreakpoints) {
      if (breakpoint in responsiveValue && responsiveValue[breakpoint] !== undefined) {
        closestBreakpointValue = responsiveValue[breakpoint];

        if (currentBreakpoint === breakpoint) {
          return closestBreakpointValue;
        }
      }
    }

    return closestBreakpointValue;
  }

  return responsiveValue;
}

export type NumericSpaceUnit = `${number}px` | `${number}rem` | `${number}%` | '0';
export type SpaceUnit = NumericSpaceUnit | 'auto' | 'fit-content' | 'initial' | `var(--${string})`;
export type ResponsiveSpace = ResponsiveValue<SpaceUnit | `${SpaceUnit} ${SpaceUnit}` | `${SpaceUnit} ${SpaceUnit} ${SpaceUnit} ${SpaceUnit}`>;

export interface BoxSizing<T extends Space | SpaceUnit = Space> {
  bottom?: T;
  left?: T;
  right?: T;
  top?: T;
}

export interface ResponsiveBoxSizing<T extends Space | SpaceUnit = Space> {
  bottom?: ResponsiveValue<T>;
  left?: ResponsiveValue<T>;
  right?: ResponsiveValue<T>;
  top?: ResponsiveValue<T>;
}

export type FlexBasis = number | `${number} ${number}` | `${number} ${number} ${'auto' | SpaceUnit}`;
export type ResponsiveFlexBasis = ResponsiveValue<FlexBasis>;

export type ResponsivePosition = ResponsiveValue<CSSProperties['position']>;

export type ResponsiveOverflow = ResponsiveValue<CSSProperties['overflow']>;
